import React from "react";
import { Link } from "react-router-dom";
import {
  IoLocationOutline,
  IoArrowForward,
  IoLockClosedOutline,
  IoShieldCheckmarkOutline,
  IoGridOutline,
  IoMapOutline,
  IoSpeedometerOutline,
  IoGitNetworkOutline,
} from "react-icons/io5";
import { BiTrip } from "react-icons/bi";
import { PiFastForwardCircle } from "react-icons/pi";
import { TbArrowsRightLeft } from "react-icons/tb";
import { listedIn } from "../../config/catalog";
import type { CatalogEntry, CatalogId, Tier } from "../../config/catalog";
import { useProductEntitlements } from "../../hooks/useProductEntitlements";
import Spinner from "../common/Spinner";
import { openDataAvailable } from "../../config/openData";
import OpenDataExampleBlock from "./OpenDataExampleBlock";
import { IS_DEMO } from "../../services/demoApi";

const LocationIcon = IoLocationOutline as any;
const TripIcon = BiTrip as any;
const FastForwardIcon = PiFastForwardCircle as any;
const FlowIcon = TbArrowsRightLeft as any;
const ArrowIcon = IoArrowForward as any;
const LockIcon = IoLockClosedOutline as any;
const ShieldIcon = IoShieldCheckmarkOutline as any;
const GridIcon = IoGridOutline as any;
const BoundaryIcon = IoMapOutline as any;
const RoadIcon = IoGitNetworkOutline as any;
const CounterIcon = IoSpeedometerOutline as any;

/** What the registry does not hold: how an entry looks on its card. */
const CARD_LOOK: Partial<Record<CatalogId, { tag: string; icon: any }>> = {
  emergency: { tag: "Managed", icon: ShieldIcon },
  traffic: { tag: "Roads", icon: FlowIcon },
  activities: { tag: "Locations", icon: LocationIcon },
  odm: { tag: "Trips", icon: TripIcon },
  realtime: { tag: "Live", icon: FastForwardIcon },
  population: { tag: "Grid", icon: GridIcon },
  workplaces: { tag: "Grid", icon: GridIcon },
  boundaries: { tag: "Polygons", icon: BoundaryIcon },
  osmRoads: { tag: "Roads", icon: RoadIcon },
  trafficCounters: { tag: "Sensors", icon: CounterIcon },
};

/**
 * Solutions are managed services built on our data (Telcofy Emergency, Telcofy Traffic); data
 * products give access to the mobility data itself; open data is public reference data (drafts,
 * no entitlement). Open data is still in development, so only demo builds show it: dev and prod
 * customers never see those cards.
 */
const ALL_GROUPS: { tier: Tier; id: string; title: string; description: string }[] = [
  { tier: "service", id: "solution", title: "Solutions", description: "Managed services built on our data." },
  { tier: "data", id: "data", title: "Data", description: "Mobility data to explore and analyse." },
  {
    tier: "open",
    id: "open",
    title: "Open data",
    description: "Public reference data, free for everyone. Draft: API coming.",
  },
];
const GROUPS = ALL_GROUPS.filter((g) => g.tier !== "open" || IS_DEMO);

/*
 * ─── NOTES (Trello #410) ──────────────────────────────────────────────────
 * - Gating is driven entirely by `GET /users/:uid/products`. A card is
 *   unlocked iff its productKey is present in the response and not switched
 *   off (`enabled: false`). The Activities and Flow cards also accept the
 *   plural spellings the data API uses ("activities", "flows"); the aliases
 *   live in ENTITLEMENT_ALIASES (src/config/products.ts).
 * - Test/internal users are handled server-side: the backend `isTest`
 *   short-circuit returns every product, so they see all cards unlocked.
 *   (No client-side admin/role check is needed.)
 * - Product key names are provisional — Alexey will finalise them at the
 *   CRM level; the data products' keys live in DATA_PRODUCT_ENTITLEMENT_KEYS.
 * - UI-only: the data APIs themselves are NOT entitlement-gated.
 * - Fail-open: if `GET /products` errors, all cards are shown active rather
 *   than locking the user out of the whole workbench.
 * ──────────────────────────────────────────────────────────────────────────
 */

/** A registry entry with no page yet: a draft card that is not a link and is never locked. */
const PlaceholderCard: React.FC<{ entry: CatalogEntry; Icon: any }> = ({ entry, Icon }) => {
  // Population and Workplaces show an example from the open data API; "API coming" stays for the rest.
  const hasExample = (entry.id === "population" || entry.id === "workplaces") && openDataAvailable();
  return (
  <div className="relative overflow-hidden rounded-3xl border border-paper-300/70 bg-surface p-7 shadow-soft">
    <div className="relative flex flex-col h-full">
      <div className="flex items-start justify-between mb-8">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
          <Icon className="h-8 w-8" />
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-note-soft px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest text-note-ink">
            Draft
          </span>
          <span className="inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest text-ink-subtle">
            Open data
          </span>
        </div>
      </div>
      <h2 className="font-display text-2xl font-bold mb-2 text-ink">{entry.title}</h2>
      <p className="text-ink-muted mb-4">{entry.blurb}</p>
      <div className="mb-6 flex-grow" />
      {hasExample ? (
        <OpenDataExampleBlock entry={entry} />
      ) : (
        <div className="text-sm font-semibold text-ink-faint">API coming</div>
      )}
    </div>
  </div>
  );
};

const ProductCards: React.FC = () => {
  const { loading, isEntryLocked } = useProductEntitlements();

  return (
    <div className="w-full">
      <div className="mb-10">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-faint mb-3">
          Workbench
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-3">
          Select a Product
        </h1>
        <p className="text-lg text-ink-muted max-w-xl">
          Choose an analytics tool to get started.
        </p>
        <Link to="/?section=status" className="mt-3 inline-flex text-sm font-semibold text-brand-600 hover:underline">
          See data status
        </Link>
      </div>

      {loading ? (
        <div className="py-16">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-12">
          {GROUPS.map((g) => (
            <section key={g.id} aria-labelledby={`group-${g.id}`}>
              <div className="mb-5">
                <h2 id={`group-${g.id}`} className="font-display text-xl font-bold text-ink">{g.title}</h2>
                <p className="text-ink-muted">{g.description}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {listedIn(g.tier).map((p) => {
                  const look = CARD_LOOK[p.id];
                  const Icon = look?.icon ?? GridIcon;
                  const tag = look?.tag ?? "";
                  const locked = isEntryLocked(p.id);

                  if (p.route === null) return <PlaceholderCard key={p.id} entry={p} Icon={Icon} />;

                  // Card inner content is shared between the active (Link) and
                  // locked (div) variants.
                  const inner = (
                    <div className="relative flex flex-col h-full">
                      <div className="flex items-start justify-between mb-8">
                        <div
                          className={`inline-flex h-16 w-16 items-center justify-center rounded-2xl transition-all duration-300 ${
                            locked
                              ? "bg-paper-200 text-ink-faint"
                              : "bg-brand-50 text-brand-500 group-hover:bg-brand-500 group-hover:text-primary-content group-hover:scale-105"
                          }`}
                        >
                          <Icon className="h-8 w-8" />
                        </div>
                        <span className="inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest text-ink-subtle">
                          {tag}
                        </span>
                      </div>

                      <h2
                        className={`font-display text-2xl font-bold mb-2 ${
                          locked ? "text-ink-subtle" : "text-ink"
                        }`}
                      >
                        {p.title}
                      </h2>
                      <p className="text-ink-muted mb-4">{p.blurb}</p>

                      <div className="mb-6 flex-grow" />

                      {locked ? (
                        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-faint">
                          <LockIcon className="h-4 w-4" />
                          Not included in your plan
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 transition-all duration-200 group-hover:gap-3">
                          Get started
                          <ArrowIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                        </div>
                      )}
                    </div>
                  );

                  if (locked) {
                    return (
                      <div
                        key={p.id}
                        aria-disabled="true"
                        title="Not included in your plan"
                        className="relative overflow-hidden rounded-3xl border border-paper-300/70 bg-paper-100/60 p-7 shadow-soft opacity-60 cursor-not-allowed"
                      >
                        {inner}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={p.id}
                      to={p.route}
                      className="group relative overflow-hidden rounded-3xl border border-paper-300/70 bg-surface p-7 shadow-soft transition-all duration-300 ease-brand hover:-translate-y-1 hover:shadow-card-hover hover:border-brand-500/40"
                    >
                      {/* Decorative gradient orb */}
                      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-brand-500/15 to-brand-mint/20 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      {inner}
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCards;
